import { Box, Typography, Stack } from "@mui/material";
import { appUrl } from "Utils/UrlUtils";
import { UnstyledLink } from "Components/Routes/UnstyledLink";
import { Carousel } from "Components/Shared/Carousel/Carousel";
import { CarouselItem } from "Components/Shared/Carousel/CarouselItem";
import { Resources, useResource } from "Translations/Resources";
import { CardChallenge } from "Components/Shared/Challenge/CardChallenge/CardChallenge";
import { SectionTitle } from "Components/Loyalty/Shared/SectionTitle/SectionTitle";
import { useLoyaltiesEventsQuery } from "Api/Queries/Loyalty/useLoyaltiesEventsQuery";
import { useMemo } from "react";
import { ErrorText } from "Components/Shared/Errors/ErrorText";
import { CardChallengeSkeleton } from "Components/Shared/Challenge/CardChallenge/CardChallengeSkeleton";
import { eventFilters } from "Components/Loyalty/Shared/Challenges/Categories/utils";

const RESOURCES = Resources.Pages.Content.Loyalty.Challenges.FeaturedChallenges;

export const FeaturedChallenges = () => {
  const { data, isLoading, isError } = useLoyaltiesEventsQuery();
  const { t } = useResource();

  const featuredEvents = useMemo(() => {
    if (!data?.events) {
      return [];
    }

    return data.events.filter(
      (event) => eventFilters.daily(event) || eventFilters.unlimited(event),
    );
  }, [data]);

  if (isLoading) {
    return (
      <Stack gap={4}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <SectionTitle>{t(RESOURCES.Title)}</SectionTitle>
          <UnstyledLink to={appUrl("all-challenges")}>
            <Typography
              component="span"
              color="primary"
              textTransform="uppercase"
              fontWeight={600}
            >
              {t(RESOURCES.AllChallenges)}
            </Typography>
          </UnstyledLink>
        </Box>
        <Carousel>
          {Array.from({ length: 3 }).map((_, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: fine here
            <CarouselItem key={index}>
              <CardChallengeSkeleton />
            </CarouselItem>
          ))}
        </Carousel>
      </Stack>
    );
  }

  if (isError || featuredEvents.length === 0) {
    return (
      <Stack gap={4}>
        <SectionTitle>{t(RESOURCES.Title)}</SectionTitle>
        <ErrorText>{t(Resources.Error.Error)}</ErrorText>
      </Stack>
    );
  }

  return (
    <Stack gap={4}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <SectionTitle>{t(RESOURCES.Title)}</SectionTitle>
        <UnstyledLink to={appUrl("all-challenges")}>
          <Typography
            component="span"
            color="primary"
            textTransform="uppercase"
            fontWeight={600}
          >
            {t(RESOURCES.AllChallenges)}
          </Typography>
        </UnstyledLink>
      </Box>
      <Carousel>
        {featuredEvents.map(
          ({ code, points, progress, isCompleted, loyaltyPointType }) => (
            <CarouselItem key={code}>
              <CardChallenge
                event={code}
                points={points}
                progressPercent={progress || 0}
                isCompleted={isCompleted}
                loyaltyPointType={loyaltyPointType}
              />
            </CarouselItem>
          ),
        )}
      </Carousel>
    </Stack>
  );
};
